import { useState, useRef } from 'react'
import type { Wine } from '../types'

type WineInput = Omit<Wine, '_key'>
import { generateMockTheme, generateMockWines, checkRapidClicks } from '../utils/devData'

const EMPTY_WINES: WineInput[] = []

type SetupDialogProps = {
  isOpen: boolean
  initialTheme?: string
  initialHost?: string
  initialDate?: string
  initialWines?: WineInput[]
  onSave: (theme: string, wines: WineInput[], host?: string, date?: string) => void
  onReset: () => void
  onClose: () => void
  isEditing: boolean
}

export function SetupDialog({
  isOpen,
  initialTheme = '',
  initialHost = '',
  initialDate = '',
  initialWines = EMPTY_WINES,
  onSave,
  onReset,
  onClose,
  isEditing,
}: SetupDialogProps) {
  const [theme, setTheme] = useState(initialTheme)
  const [host, setHost] = useState(initialHost)
  const [date, setDate] = useState(initialDate)
  const [wines, setWines] = useState<WineInput[]>(
    initialWines.length > 0 ? [...initialWines, { name: '' }] : [{ name: '' }],
  )
  const [showResetConfirm, setShowResetConfirm] = useState(false)
  const [showMockConfirm, setShowMockConfirm] = useState(false)
  const [prevIsOpen, setPrevIsOpen] = useState(isOpen)
  const clickTimestamps = useRef<number[]>([])

  // Reset state when dialog opens (update during render pattern)
  if (prevIsOpen !== isOpen) {
    setPrevIsOpen(isOpen)
    if (isOpen) {
      setTheme(initialTheme)
      setHost(initialHost)
      setDate(initialDate)
      setWines(initialWines.length > 0 ? [...initialWines, { name: '' }] : [{ name: '' }])
      setShowResetConfirm(false)
      setShowMockConfirm(false)
    }
  }

  if (!isOpen) return null

  const canClose = theme.trim() !== '' && wines.some((w) => w.name.trim() !== '')

  const autoSave = (newTheme: string, newWines: WineInput[], newHost = host, newDate = date) => {
    const validWines = newWines.filter((w) => w.name.trim() !== '')
    if (newTheme.trim() && validWines.length > 0) {
      onSave(newTheme.trim(), validWines, newHost.trim() || undefined, newDate || undefined)
    }
  }

  const handleThemeChange = (value: string) => {
    setTheme(value)
    autoSave(value, wines)
  }

  const handleHostChange = (value: string) => {
    setHost(value)
    autoSave(theme, wines, value, date)
  }

  const handleDateChange = (value: string) => {
    setDate(value)
    autoSave(theme, wines, host, value)
  }

  const handleWineChange = (index: number, field: keyof WineInput, value: string) => {
    const updated = [...wines]
    if (field === 'name') {
      updated[index] = { ...updated[index], name: value }
      // Auto-add new row when last wine gets a name
      if (index === updated.length - 1 && value.trim() !== '') {
        updated.push({ name: '' })
      }
    } else if (field === 'price') {
      const numValue = value === '' ? undefined : parseFloat(value)
      updated[index] = {
        ...updated[index],
        price: Number.isNaN(numValue) ? undefined : numValue,
      }
    }
    setWines(updated)
    autoSave(theme, updated)
  }

  const removeWine = (index: number) => {
    if (wines.length > 1) {
      const updated = wines.filter((_, i) => i !== index)
      setWines(updated)
      autoSave(theme, updated)
    }
  }

  const handleResetClick = () => {
    setShowResetConfirm(true)
  }

  const handleResetConfirm = () => {
    setTheme('')
    setHost('')
    setDate('')
    setWines([{ name: '' }])
    setShowResetConfirm(false)
    onReset()
  }

  const handleCloseWrapperClick = () => {
    if (canClose) return

    if (checkRapidClicks(clickTimestamps.current)) {
      const mockTheme = generateMockTheme()
      const mockWines = [...generateMockWines(Math.random() < 0.5 ? 5 : 6), { name: '' }]
      setTheme(mockTheme)
      setWines(mockWines)
      autoSave(mockTheme, mockWines)
      setShowMockConfirm(true)
      setTimeout(() => setShowMockConfirm(false), 3000)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-cream rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <h2 className="text-xl font-bold mb-4">{isEditing ? 'Edit Tasting' : 'New Tasting'}</h2>

          {showMockConfirm ? (
            <div className="py-8 text-center">
              <p className="text-xl font-medium text-black/80">Data mocked!</p>
              <p className="text-sm text-black/50 mt-2">Loading in a moment...</p>
            </div>
          ) : showResetConfirm ? (
            <div className="space-y-4">
              <p className="text-black/80">
                Are you sure you want to reset? This will delete all tasting data including scores.
              </p>
              <div className="flex gap-2">
                <button
                  onClick={handleResetConfirm}
                  className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                >
                  Yes, Reset
                </button>
                <button
                  onClick={() => setShowResetConfirm(false)}
                  className="px-4 py-2 bg-black/10 rounded hover:bg-black/20"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Theme</label>
                  <input
                    type="text"
                    value={theme}
                    onChange={(e) => handleThemeChange(e.target.value)}
                    placeholder="e.g., Summer Reds, Bordeaux 2020"
                    className="w-full px-3 py-2 border border-black/20 rounded bg-white focus:outline-none focus:border-black"
                  />
                </div>

                <div className="flex gap-3">
                  <div className="flex-1">
                    <label className="block text-sm font-medium mb-1">Host</label>
                    <input
                      type="text"
                      value={host}
                      onChange={(e) => handleHostChange(e.target.value)}
                      placeholder="Optional"
                      className="w-full px-3 py-2 border border-black/20 rounded bg-white focus:outline-none focus:border-black"
                    />
                  </div>
                  <div className="w-40">
                    <label className="block text-sm font-medium mb-1">Date</label>
                    <input
                      type="date"
                      value={date}
                      onChange={(e) => handleDateChange(e.target.value)}
                      className="w-full px-3 py-2 border border-black/20 rounded bg-white focus:outline-none focus:border-black"
                    />
                  </div>
                </div>

                <div className="pt-2 space-y-4">
                    {wines.map((wine, index) => (
                      <div key={index}>
                        <div className="flex justify-between items-center mb-1">
                          <label className="text-sm font-medium text-black/50">
                            Wine {index + 1}
                          </label>
                          <button
                            onClick={() => removeWine(index)}
                            disabled={wines.length <= 1}
                            className="text-sm text-black/40 hover:text-black disabled:opacity-30"
                          >
                            Remove
                          </button>
                        </div>
                        <div className="flex flex-col sm:flex-row gap-2">
                          <input
                            type="text"
                            value={wine.name}
                            onChange={(e) => handleWineChange(index, 'name', e.target.value)}
                            placeholder="Wine name"
                            className="flex-1 px-3 py-2 border border-black/20 rounded bg-white focus:outline-none focus:border-black"
                          />
                          <div className="flex w-24 shrink-0">
                            <span className="flex items-center px-2 py-2 border border-r-0 border-black/20 rounded-l bg-black/5 text-black/50 text-sm">
                              $
                            </span>
                            <input
                              type="text"
                              value={wine.price ?? ''}
                              onChange={(e) => handleWineChange(index, 'price', e.target.value)}
                              placeholder="Price"
                              className="w-full px-2 py-2 border border-black/20 rounded-r bg-white focus:outline-none focus:border-black"
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                </div>
              </div>

              <div className="flex justify-between mt-6 pt-4 border-t border-black/10">
                <div>
                  {isEditing && (
                    <button
                      onClick={handleResetClick}
                      className="px-4 py-2 text-red-600 hover:text-red-700"
                    >
                      Reset
                    </button>
                  )}
                </div>
                <div onClick={handleCloseWrapperClick}>
                  <button
                    onClick={onClose}
                    disabled={!canClose}
                    className="px-4 py-2 bg-black/10 rounded hover:bg-black/20 disabled:opacity-50 disabled:cursor-not-allowed disabled:pointer-events-none"
                  >
                    Close
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
